import React, { FC, useState, useEffect, useRef } from "react";
import { useQuery } from "@apollo/client";
import { Typography, Card, Space, Button, Input, Spin, Tag, Progress, Cascader } from "antd";
import { ForwardOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import styles from "./create-proposal.css";
import { BEDROCK_SUGGEST_PRACTICE_AREAS_QUERY, ORGANISATIONAL_UNITS_QUERY } from "~/graphql/query";
import { useCurrentUserData } from "~/hooks";
import { Userpilot } from "userpilot";

const {
  ["clarifying-questions-container-full"]: clarifyingQuestionsContainerFullClassName,
  ["clarifying-questions-error"]: clarifyingQuestionsErrorClassName,
  ["clarifying-questions-layout"]: clarifyingQuestionsLayoutClassName,
  ["clarifying-questions-content"]: clarifyingQuestionsContentClassName,
  ["clarifying-questions-sidebar"]: clarifyingQuestionsSidebarClassName,
  ["clarifying-questions-header-sidebar"]: clarifyingQuestionsHeaderSidebarClassName,
  ["clarifying-questions-card"]: clarifyingQuestionsCardClassName,
  ["clarifying-questions-loading"]: clarifyingQuestionsLoadingClassName,
  ["clarifying-questions-loading-question"]: clarifyingQuestionsLoadingQuestionClassName,
  ["clarifying-questions-loading-title"]: clarifyingQuestionsLoadingTitleClassName,
  ["clarifying-questions-loading-text"]: clarifyingQuestionsLoadingTextClassName,
  ["clarifying-questions-title"]: clarifyingQuestionsTitleClassName,
  ["clarifying-questions-options"]: clarifyingQuestionsOptionsClassName,
  ["clarifying-questions-options-space"]: clarifyingQuestionsOptionsSpaceClassName,
  ["clarifying-questions-option"]: clarifyingQuestionsOptionClassName,
  ["clarifying-questions-textarea"]: clarifyingQuestionsTextareaClassName,
  ["clarifying-questions-buttons"]: clarifyingQuestionsButtonsClassName,
  ["clarifying-questions-next-button"]: clarifyingQuestionsNextButtonClassName,
  ["clarifying-questions-skip-button"]: clarifyingQuestionsSkipButtonClassName,
  ["clarifying-questions-info"]: clarifyingQuestionsInfoClassName,
  ["clarifying-questions-info-text"]: clarifyingQuestionsInfoTextClassName,
  ["clarifying-questions-info-button"]: clarifyingQuestionsInfoButtonClassName,
  ["clarifying-questions-header-title"]: clarifyingQuestionsHeaderTitleClassName,
  ["clarifying-questions-header-subtitle"]: clarifyingQuestionsHeaderSubtitleClassName,
  ["clarifying-questions-option-selected"]: clarifyingQuestionsOptionSelectedClassName,
  ["clarifying-questions-scrollable-content"]: clarifyingQuestionsScrollableContentClassName,
  ["clarifying-questions-practice-note"]: clarifyingQuestionsPracticeNoteClassName,
  ["clarifying-questions-practice-note-visible"]: clarifyingQuestionsPracticeNoteVisibleClassName,
  ["clarifying-questions-practice-note-text"]: clarifyingQuestionsPracticeNoteTextClassName,
  ["clarifying-questions-practice-cascader"]: clarifyingQuestionsPracticeCascaderClassName,
} = styles;

interface StreamingState {
  sessionId: string | null;
  streamingQuestions: any[];
  totalQuestions: number | null;
  isComplete: boolean;
  error: string | null;
  streamingActive: boolean;
}

interface ClarifyingQuestionsSectionProps {
  currentQuestionIndex: number;
  selectedAnswers: string[];
  otherText: string;
  onAnswerChange: (values: string[]) => void;
  onOtherTextChange: (value: string) => void;
  onSkipQuestions: () => void;
  onGoBack: () => void;
  // New streaming props
  streamingState?: StreamingState;
  onAutoAdvance?: (answerValues?: string[]) => void;
  onSkipQuestion?: () => void;
  matterTitle: string;
  onPracticeAreaSelect?: (practiceAreaId: number, parentId?: number | null) => void;
  onPracticeAreaClear?: () => void;
}

export const ClarifyingQuestionsSection: FC<ClarifyingQuestionsSectionProps> = ({
  currentQuestionIndex,
  selectedAnswers,
  otherText,
  onAnswerChange,
  onOtherTextChange,
  onSkipQuestions,
  onGoBack,
  streamingState,
  onAutoAdvance,
  onSkipQuestion,
  matterTitle,
  onPracticeAreaSelect,
  onPracticeAreaClear,
}) => {
  const { currentUser } = useCurrentUserData({ cache: true });
  const questions = streamingState?.streamingQuestions || [];
  const [validationError, setValidationError] = useState<string | null>(null);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const otherTextAreaRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState({
    hasScrolled: false,
    hasMoreContent: false,
  });

  // Local first-step state: practice area selection
  const [showPracticeAreaStep, setShowPracticeAreaStep] = useState<boolean>(true);
  const [selectedPracticeAreaId, setSelectedPracticeAreaId] = useState<number | null>(null);
  const [selectedPracticeAreaParentId, setSelectedPracticeAreaParentId] = useState<
    number | undefined
  >(undefined);
  const [practiceAreaAutoSkipped, setPracticeAreaAutoSkipped] = useState<boolean>(false);
  const [practiceAreaManuallyOverridden, setPracticeAreaManuallyOverridden] =
    useState<boolean>(false);

  // Fetch suggested practice areas for the first question
  const suggestPracticeAreasSkip = !showPracticeAreaStep || !matterTitle;
  const suggestPracticeAreasVars = {
    matterTitle,
    practiceAreaName: currentUser?.practiceAreaName ?? null,
  };
  const {
    data: practiceAreasData,
    loading: practiceAreasLoading,
    error: practiceAreasError,
  } = useQuery(BEDROCK_SUGGEST_PRACTICE_AREAS_QUERY, {
    variables: suggestPracticeAreasVars,
    skip: suggestPracticeAreasSkip,
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
  });

  const practiceAreas = practiceAreasData?.bedrockSuggestPracticeAreas?.organisationalUnits || [];

  // Fetch all practice areas for the cascader
  const { data: allPaData, loading: allPaLoading } = useQuery(ORGANISATIONAL_UNITS_QUERY, {
    // Load options during the PA step, and also after if a PA was set (auto or user)
    skip: !(showPracticeAreaStep || practiceAreaAutoSkipped || selectedPracticeAreaId !== null),
    fetchPolicy: "cache-first",
  });
  const allPracticeAreas = allPaData?.organisationalUnits || [];

  const allPaOptions = Array.isArray(allPracticeAreas)
    ? allPracticeAreas
        .filter((x: any) => !x?.parent?.id)
        .map((pa: any) => ({
          value: Number(pa.id),
          label: pa.name,
          children:
            allPracticeAreas
              .filter((x: any) => x?.parent?.id === pa.id)
              .map((y: any) => ({ value: Number(y.id), label: y.name })) || [],
        }))
    : [];

  const currentQuestion = questions[currentQuestionIndex];

  // Clear validation error when inputs change
  useEffect(() => {
    if (validationError) {
      setValidationError(null);
    }
  }, [selectedAnswers, otherText]);

  // Scroll detection for fade indicators
  const handleScroll = () => {
    const element = scrollableRef.current;
    if (element) {
      const { scrollTop, scrollHeight, clientHeight } = element;
      const hasScrolled = scrollTop > 10;
      const hasMoreContent = scrollTop + clientHeight < scrollHeight - 10;

      setScrollState({
        hasScrolled,
        hasMoreContent,
      });
    }
  };

  // Auto-skip practice area step when only one option is returned
  useEffect(() => {
    if (
      showPracticeAreaStep &&
      !practiceAreasLoading &&
      !practiceAreasError &&
      Array.isArray(practiceAreas) &&
      practiceAreas.length === 1
    ) {
      const unit = practiceAreas[0] as any;
      const parentId = unit?.parent?.id ? Number(unit.parent.id) : undefined;
      const idNum = Number(unit?.id);
      if (idNum && onPracticeAreaSelect) {
        onPracticeAreaSelect(idNum, parentId);
      }
      // Track locally so we can show the sidebar note and cascader
      setSelectedPracticeAreaId(idNum || null);
      setSelectedPracticeAreaParentId(parentId);
      setPracticeAreaAutoSkipped(true);
      setPracticeAreaManuallyOverridden(false);
      setShowPracticeAreaStep(false);
    }
  }, [
    showPracticeAreaStep,
    practiceAreasLoading,
    practiceAreasError,
    practiceAreas,
    onPracticeAreaSelect,
  ]);

  useEffect(() => {
    const element = scrollableRef.current;
    if (element) {
      handleScroll();
      element.addEventListener("scroll", handleScroll);
      return () => element.removeEventListener("scroll", handleScroll);
    }
  }, [currentQuestion]);

  useEffect(() => {
    const element = scrollableRef.current;
    if (element) {
      setTimeout(handleScroll, 50);
    }
  }, [selectedAnswers.includes("other")]);

  useEffect(() => {
    if (
      selectedAnswers.includes("other") &&
      currentQuestion?.allowOther &&
      otherTextAreaRef.current
    ) {
      setTimeout(() => {
        otherTextAreaRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  }, [selectedAnswers.includes("other"), currentQuestion?.allowOther]);

  // Check if we need to show a spinner for the current question
  const needsLoadingSpinner =
    !currentQuestion &&
    // Show spinner if streaming is active
    (streamingState?.streamingActive ||
      // OR if we're waiting for more questions (streaming stopped but not complete, and we're past current questions)
      (!streamingState?.isComplete &&
        streamingState?.totalQuestions !== null &&
        currentQuestionIndex < (streamingState?.totalQuestions || 0)));

  const handleCheckboxChange = (value: string, checked: boolean) => {
    let newAnswers: string[];

    if (checked) {
      // Add the value if checked
      newAnswers = [...selectedAnswers, value];
    } else {
      // Remove the value if unchecked
      newAnswers = selectedAnswers.filter((answer) => answer !== value);

      // If deselecting "other", also clear the other text
      if (value === "other") {
        onOtherTextChange("");
      }
    }

    onAnswerChange(newAnswers);
  };

  const handleNextClick = () => {
    // Validation logic
    const isFreefrom =
      (!currentQuestion.options || currentQuestion.options.length === 0) &&
      currentQuestion.allowOther;

    if (!isFreefrom) {
      // For multiple choice questions only
      if (selectedAnswers.length === 0) {
        setValidationError(
          "Please select at least one option before continuing, or skip this question."
        );
        return;
      }

      // If "other" is selected, require otherText
      if (selectedAnswers.includes("other") && !otherText?.trim()) {
        setValidationError("Please provide an answer before continuing.");
        return;
      }
    }

    // If validation passes, proceed
    if (onAutoAdvance) {
      onAutoAdvance(isFreefrom ? ["other"] : selectedAnswers);
    }
  };

  // Calculate progress percentage
  const calculateProgress = () => {
    // Count the practice area step as a completed step ONLY if it was shown (not auto-skipped)
    const practiceStepCompletedAndCounted = !showPracticeAreaStep && !practiceAreaAutoSkipped;
    const completedStreamingQuestions = currentQuestionIndex; // completed before current
    const completedSteps = (practiceStepCompletedAndCounted ? 1 : 0) + completedStreamingQuestions;

    const baseProgress = Math.min(85, completedSteps * 20); // 20% per step, capped at 85%

    // If we have the current question, check if it's the final question
    if (currentQuestion) {
      const isFinalQuestion =
        (!currentQuestion.options || currentQuestion.options.length === 0) &&
        currentQuestion.allowOther;

      if (isFinalQuestion) {
        return 90; // Show 90% for the final question
      }
    }

    return baseProgress;
  };

  const progressPercent = calculateProgress();

  // Show initial loading state when no questions have been loaded yet
  const isInitialLoading = streamingState?.streamingActive && questions.length === 0;

  if (!showPracticeAreaStep && !currentQuestion && !needsLoadingSpinner && !isInitialLoading) {
    return null;
  }

  return (
    <div className={clarifyingQuestionsContainerFullClassName}>
      {/* Status Display */}
      {streamingState?.error && (
        <div className={clarifyingQuestionsErrorClassName}>
          <Tag color="red">Error: {streamingState.error}</Tag>
        </div>
      )}

      {/* Main horizontal layout */}
      <div className={clarifyingQuestionsLayoutClassName}>
        {/* Questions section - Left side (2/3) */}
        <div className={clarifyingQuestionsContentClassName}>
          <Card className={clarifyingQuestionsCardClassName}>
            {(!isInitialLoading || (showPracticeAreaStep && !practiceAreasLoading)) && (
              <div style={{ marginBottom: "20px" }}>
                <Progress
                  percent={progressPercent}
                  showInfo={false}
                  strokeColor={{
                    "0%": "#40a9ff",
                    "100%": "#1890ff",
                  }}
                  trailColor="#f0f0f0"
                  strokeWidth={6}
                />
              </div>
            )}

            {showPracticeAreaStep ? (
              <>
                {practiceAreasLoading && (!practiceAreas || practiceAreas.length === 0) ? (
                  <div className={clarifyingQuestionsLoadingClassName} style={{ height: "600px" }}>
                    <Spin size="large" />
                    <Typography.Title
                      level={4}
                      className={clarifyingQuestionsLoadingTitleClassName}
                    >
                      {"Thinking about your matter..."}
                    </Typography.Title>
                    <Typography.Text
                      type="secondary"
                      className={clarifyingQuestionsLoadingTextClassName}
                    >
                      We're generating questions to help us understand your matter better.
                    </Typography.Text>
                  </div>
                ) : (
                  <>
                    <Typography.Title level={4} className={clarifyingQuestionsTitleClassName}>
                      Which practice area best fits this matter?
                    </Typography.Title>

                    {practiceAreas?.length > 0 && (
                      <div style={{ minHeight: "38px", marginBottom: "16px" }}>
                        <Typography.Text type="secondary" style={{ display: "block" }}>
                          Select one
                        </Typography.Text>
                      </div>
                    )}

                    <div
                      ref={scrollableRef}
                      className={clarifyingQuestionsScrollableContentClassName}
                      style={{
                        boxShadow:
                          [
                            scrollState.hasScrolled
                              ? "inset 0 40px 40px -30px rgba(0,0,0,0.25)"
                              : "",
                            scrollState.hasMoreContent
                              ? "inset 0 -40px 40px -30px rgba(0,0,0,0.25)"
                              : "",
                          ]
                            .filter(Boolean)
                            .join(", ") || "none",
                        height: "420px",
                        marginBottom: "4px",
                      }}
                    >
                      <div className={clarifyingQuestionsOptionsClassName}>
                        <Space
                          direction="vertical"
                          className={clarifyingQuestionsOptionsSpaceClassName}
                          size="small"
                        >
                          {practiceAreas?.map((unit: any) => (
                            <div
                              key={unit.id}
                              onClick={() => {
                                setSelectedPracticeAreaId(Number(unit.id));
                                setSelectedPracticeAreaParentId(
                                  unit?.parent?.id ? Number(unit.parent.id) : undefined
                                );
                              }}
                              className={`${clarifyingQuestionsOptionClassName} ${
                                selectedPracticeAreaId === Number(unit.id)
                                  ? clarifyingQuestionsOptionSelectedClassName
                                  : ""
                              }`}
                            >
                              {unit.name}
                            </div>
                          ))}
                          {practiceAreas?.length > 0 && (
                            <Typography.Text type="secondary" style={{ display: "block" }}>
                              Or choose a different practice area
                            </Typography.Text>
                          )}
                          <div style={{ width: "100%", marginTop: "16px" }}>
                            <Cascader
                              showSearch
                              options={allPaOptions}
                              loading={allPaLoading}
                              placeholder="Select practice area"
                              onChange={(path: any) => {
                                const parentId =
                                  Array.isArray(path) && path.length === 2
                                    ? Number(path[0])
                                    : undefined;
                                const id =
                                  Array.isArray(path) && path.length > 0
                                    ? Number(path[path.length - 1])
                                    : undefined;
                                setSelectedPracticeAreaParentId(parentId);
                                setSelectedPracticeAreaId(id ?? null);
                              }}
                              style={{ width: "100%" }}
                            />
                          </div>
                        </Space>
                      </div>
                    </div>

                    <div style={{ flexShrink: 0, marginTop: "auto" }}>
                      <div style={{ minHeight: "22px", marginBottom: "4px" }}>
                        {practiceAreasError && (
                          <Typography.Text type="danger" style={{ display: "block" }}>
                            Failed to load suggestions. You can skip this question.
                          </Typography.Text>
                        )}
                      </div>

                      <div className={clarifyingQuestionsButtonsClassName}>
                        {/* No back button on the first step */}

                        <div style={{ display: "flex", gap: "8px", marginLeft: "auto" }}>
                          <Button
                            onClick={() => {
                              // Skip this first question without storing an answer
                              onPracticeAreaClear?.();
                              setPracticeAreaAutoSkipped(false);
                              setShowPracticeAreaStep(false);
                            }}
                            className={clarifyingQuestionsSkipButtonClassName}
                          >
                            Skip question
                          </Button>

                          <Button
                            type="primary"
                            onClick={() => {
                              // Proceed to streaming questions; answer storage intentionally unimplemented
                              if (selectedPracticeAreaId && onPracticeAreaSelect) {
                                onPracticeAreaSelect(
                                  selectedPracticeAreaId,
                                  selectedPracticeAreaParentId
                                );
                              }
                              setPracticeAreaAutoSkipped(false);
                              setShowPracticeAreaStep(false);
                            }}
                            className={clarifyingQuestionsNextButtonClassName}
                            disabled={!selectedPracticeAreaId}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : isInitialLoading ? (
              <div className={clarifyingQuestionsLoadingClassName} style={{ height: "600px" }}>
                <Spin size="large" />
                <Typography.Title level={4} className={clarifyingQuestionsLoadingTitleClassName}>
                  {"Thinking about your matter..."}
                </Typography.Title>
                <Typography.Text
                  type="secondary"
                  className={clarifyingQuestionsLoadingTextClassName}
                >
                  We're generating questions to help us understand your matter better.
                </Typography.Text>
              </div>
            ) : needsLoadingSpinner ? (
              <div
                className={clarifyingQuestionsLoadingQuestionClassName}
                style={{ height: "580px" }}
              >
                <Spin size="large" />
                <Typography.Title level={4} className={clarifyingQuestionsLoadingTitleClassName}>
                  Loading question {currentQuestionIndex + 1}...
                </Typography.Title>
                <Typography.Text
                  type="secondary"
                  className={clarifyingQuestionsLoadingTextClassName}
                >
                  This question is being generated. Please wait.
                </Typography.Text>
              </div>
            ) : (
              <>
                <Typography.Title level={4} className={clarifyingQuestionsTitleClassName}>
                  {currentQuestion.label}
                </Typography.Title>

                <div style={{ minHeight: "38px", marginBottom: "16px" }}>
                  {!(
                    (!currentQuestion.options || currentQuestion.options.length === 0) &&
                    currentQuestion.allowOther
                  ) && (
                    <Typography.Text type="secondary" style={{ display: "block" }}>
                      Select all that apply
                    </Typography.Text>
                  )}
                </div>

                <div
                  ref={scrollableRef}
                  className={clarifyingQuestionsScrollableContentClassName}
                  style={{
                    boxShadow:
                      [
                        scrollState.hasScrolled ? "inset 0 40px 40px -30px rgba(0,0,0,0.25)" : "",
                        scrollState.hasMoreContent
                          ? "inset 0 -40px 40px -30px rgba(0,0,0,0.25)"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(", ") || "none",
                    height: "420px",
                    marginBottom: "4px",
                  }}
                >
                  <div className={clarifyingQuestionsOptionsClassName}>
                    {(!currentQuestion.options || currentQuestion.options.length === 0) &&
                    currentQuestion.allowOther ? (
                      <Input.TextArea
                        placeholder={
                          currentQuestion.otherPlaceholder || "Please provide your answer..."
                        }
                        value={otherText}
                        onChange={(e) => onOtherTextChange(e.target.value)}
                        className={clarifyingQuestionsTextareaClassName}
                        style={{ height: "350px", resize: "none" }}
                        autoFocus
                        status={validationError ? "error" : undefined}
                      />
                    ) : (
                      <>
                        <Space
                          direction="vertical"
                          className={clarifyingQuestionsOptionsSpaceClassName}
                          size="small"
                        >
                          {currentQuestion.options?.map((option: any) => (
                            <div
                              key={option.value}
                              onClick={() =>
                                handleCheckboxChange(
                                  option.value,
                                  !selectedAnswers.includes(option.value)
                                )
                              }
                              className={`${clarifyingQuestionsOptionClassName} ${
                                selectedAnswers.includes(option.value)
                                  ? clarifyingQuestionsOptionSelectedClassName
                                  : ""
                              }`}
                            >
                              {option.label}
                            </div>
                          ))}
                          {currentQuestion.allowOther && (
                            <div
                              onClick={() =>
                                handleCheckboxChange("other", !selectedAnswers.includes("other"))
                              }
                              className={`${clarifyingQuestionsOptionClassName} ${
                                selectedAnswers.includes("other")
                                  ? clarifyingQuestionsOptionSelectedClassName
                                  : ""
                              }`}
                            >
                              Other
                            </div>
                          )}
                        </Space>

                        {selectedAnswers.includes("other") && currentQuestion.allowOther && (
                          <div ref={otherTextAreaRef}>
                            <Input.TextArea
                              placeholder={currentQuestion.otherPlaceholder || "Please specify..."}
                              value={otherText}
                              onChange={(e) => onOtherTextChange(e.target.value)}
                              className={clarifyingQuestionsTextareaClassName}
                              rows={3}
                              style={{ marginTop: "12px" }}
                              status={
                                validationError && selectedAnswers.includes("other")
                                  ? "error"
                                  : undefined
                              }
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div style={{ flexShrink: 0, marginTop: "auto" }}>
                  <div style={{ minHeight: "22px", marginBottom: "4px" }}>
                    {validationError && (
                      <Typography.Text type="danger" style={{ display: "block" }}>
                        {validationError}
                      </Typography.Text>
                    )}
                  </div>

                  <div className={clarifyingQuestionsButtonsClassName}>
                    {(currentQuestionIndex > 0 ||
                      (!showPracticeAreaStep &&
                        currentQuestionIndex === 0 &&
                        !practiceAreaAutoSkipped)) && (
                      <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => {
                          if (currentQuestionIndex > 0) {
                            onGoBack();
                          } else {
                            // Return to practice area selection step
                            setShowPracticeAreaStep(true);
                          }
                        }}
                        className={clarifyingQuestionsSkipButtonClassName}
                      >
                        Back
                      </Button>
                    )}

                    <div style={{ display: "flex", gap: "8px", marginLeft: "auto" }}>
                      <Button
                        onClick={() => {
                          // Skip this question - don't store any answer
                          if (onSkipQuestion) {
                            onSkipQuestion();
                          }
                        }}
                        className={clarifyingQuestionsSkipButtonClassName}
                      >
                        Skip question
                      </Button>

                      <Button
                        type="primary"
                        onClick={handleNextClick}
                        className={clarifyingQuestionsNextButtonClassName}
                      >
                        {(!currentQuestion.options || currentQuestion.options.length === 0) &&
                        currentQuestion.allowOther
                          ? "Finish"
                          : "Next"}
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </Card>
        </div>

        {/* Header section - Right side (1/3) */}
        <div className={clarifyingQuestionsSidebarClassName}>
          <div className={clarifyingQuestionsHeaderSidebarClassName}>
            <Typography.Title level={3} className={clarifyingQuestionsHeaderTitleClassName}>
              Help us understand your matter better
            </Typography.Title>
            <Typography.Text
              type="secondary"
              className={clarifyingQuestionsHeaderSubtitleClassName}
            >
              Answer a few quick questions to get more relevant suggestions
            </Typography.Text>

            <div className={clarifyingQuestionsInfoClassName}>
              <Typography.Text type="secondary" className={clarifyingQuestionsInfoTextClassName}>
                Don't have time for questions? You can skip this section and we'll generate
                suggestions based on your matter title.
              </Typography.Text>
              <Button
                icon={<ForwardOutlined />}
                onClick={() => {
                  Userpilot.track("skip_all_questions");
                  onSkipQuestions();
                }}
                block
                className={clarifyingQuestionsInfoButtonClassName}
              >
                Skip all questions
              </Button>
            </div>
            {(() => {
              const practiceNoteVisible =
                !showPracticeAreaStep &&
                (practiceAreaAutoSkipped || selectedPracticeAreaId !== null);
              return (
                <div
                  aria-hidden={!practiceNoteVisible}
                  className={`${clarifyingQuestionsPracticeNoteClassName} ${
                    practiceNoteVisible ? clarifyingQuestionsPracticeNoteVisibleClassName : ""
                  }`}
                >
                  <Typography.Text
                    type="secondary"
                    className={clarifyingQuestionsPracticeNoteTextClassName}
                  >
                    {practiceAreaManuallyOverridden || !practiceAreaAutoSkipped
                      ? "Using selected practice area:"
                      : "Auto detected practice area:"}
                  </Typography.Text>
                  <Cascader
                    showSearch
                    options={allPaOptions}
                    loading={allPaLoading}
                    placeholder="Change practice area"
                    value={
                      selectedPracticeAreaId
                        ? selectedPracticeAreaParentId
                          ? [selectedPracticeAreaParentId, selectedPracticeAreaId]
                          : [selectedPracticeAreaId]
                        : undefined
                    }
                    onChange={(path: any) => {
                      const parentId =
                        Array.isArray(path) && path.length === 2 ? Number(path[0]) : undefined;
                      const id =
                        Array.isArray(path) && path.length > 0
                          ? Number(path[path.length - 1])
                          : undefined;
                      setSelectedPracticeAreaParentId(parentId);
                      setSelectedPracticeAreaId(id ?? null);
                      if (id && onPracticeAreaSelect) {
                        onPracticeAreaSelect(id, parentId);
                        // Treat as user-selected for messaging, but do not affect progress step
                        setPracticeAreaManuallyOverridden(true);
                      }
                    }}
                    className={clarifyingQuestionsPracticeCascaderClassName}
                  />
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};
